import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, KeyRound, Trash2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const roleColors = { manager: 'bg-gold/20 text-gold', director: 'bg-purple-500/20 text-purple-400', chef: 'bg-amber-500/20 text-amber-400', bartender: 'bg-blue-accent/20 text-blue-accent', shisha_expert: 'bg-pink-500/20 text-pink-400', waiter: 'bg-emerald-500/20 text-emerald-400', kiosk: 'bg-zinc-500/20 text-zinc-300' };

// Roles that clock in/out at the kiosk (own a 4-digit PIN)
const PINNED_ROLES = new Set(['manager', 'director', 'chef', 'bartender', 'shisha_expert', 'waiter']);

export default function StaffPage() {
  const { user: currentUser } = useAuth();
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'waiter' });
  const [formOpen, setFormOpen] = useState(false);
  const [pinTarget, setPinTarget] = useState(null); // staff member whose PIN we're editing
  const [pinDraft, setPinDraft] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchStaff = useCallback(async () => {
    try { const { data } = await api.get('/staff'); setStaff(data); } catch (err) { console.error('[StaffPage.js] error:', err); }
  }, []);
  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const createStaff = async () => {
    try {
      await api.post('/auth/register', form);
      toast.success('Staff member created');
      setFormOpen(false); setForm({ name: '', email: '', password: '', role: 'waiter' }); fetchStaff();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const savePin = async () => {
    if (!/^\d{4}$/.test(pinDraft)) { toast.error('PIN must be exactly 4 digits'); return; }
    setPinSaving(true);
    try {
      await api.post('/staff/kiosk/pin', { user_id: pinTarget.id, pin: pinDraft });
      toast.success(`Kiosk PIN updated for ${pinTarget.name}`);
      setPinTarget(null); setPinDraft('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setPinSaving(false); }
  };

  const deleteStaff = async (s) => {
    if (!window.confirm(`Remove ${s.name || s.email} from staff? This cannot be undone.`)) return;
    setDeletingId(s.id);
    try {
      await api.delete(`/staff/${s.id}`);
      toast.success(`Removed ${s.name || s.email}`);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    } finally { setDeletingId(null); }
  };

  return (
    <div className="animate-fadeIn">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm font-sans text-zinc-400">{staff.length} staff members</p>
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-gold text-black rounded-sm font-sans text-xs font-bold" data-testid="add-staff-button"><Plus className="w-3 h-3 mr-1" /> Add Staff</Button>
          </DialogTrigger>
          <DialogContent className="bg-bg-secondary border border-white/10 max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-white">New Staff Member</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Name</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-email-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Password</Label>
                <Input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-password-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Role</Label>
                <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                  <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-bg-secondary border border-white/10">
                    <SelectItem value="waiter">Waiter</SelectItem>
                    <SelectItem value="bartender">Bartender</SelectItem>
                    <SelectItem value="chef">Chef</SelectItem>
                    <SelectItem value="shisha_expert">Shisha Expert</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="director">Director</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={createStaff} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="create-staff-button">Create Staff Member</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {staff.map(s => {
          // Manager can't delete themselves, can't delete director/kiosk, and can't
          // delete another manager unless they're director.
          const canDelete = currentUser
            && s.id !== currentUser.id
            && s.role !== 'director'
            && s.role !== 'kiosk'
            && !(s.role === 'manager' && currentUser.role !== 'director');
          return (
          <div key={s.id} className="flex items-center gap-3 p-4 bg-white/[0.03] border border-white/5 rounded-sm card-hover" data-testid={`staff-member-${s.id}`}>
            <div className="w-10 h-10 rounded-sm bg-white/5 flex items-center justify-center text-sm font-sans text-zinc-400 font-bold">{s.name?.[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-sans text-sm truncate">{s.name}</p>
              <p className="text-zinc-500 font-sans text-xs truncate">{s.email}</p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <Badge className={`${roleColors[s.role] || 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-xs`}>{s.role}</Badge>
              <div className="flex items-center gap-2">
                {PINNED_ROLES.has(s.role) && (
                  <button onClick={() => { setPinTarget(s); setPinDraft(''); }}
                    className="text-[10px] font-sans text-zinc-500 hover:text-gold flex items-center gap-1"
                    data-testid={`set-pin-${s.id}`}>
                    <KeyRound className="w-3 h-3" /> PIN
                  </button>
                )}
                {canDelete && (
                  <button onClick={() => deleteStaff(s)}
                    disabled={deletingId === s.id}
                    className="text-[10px] font-sans text-zinc-500 hover:text-red-400 flex items-center gap-1 disabled:opacity-50"
                    data-testid={`delete-staff-${s.id}`}>
                    {deletingId === s.id ? (
                      <span className="w-3 h-3 border border-red-400/40 border-t-red-400 rounded-full animate-spin" />
                    ) : (
                      <><Trash2 className="w-3 h-3" /> Remove</>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Kiosk PIN editor */}
      <Dialog open={!!pinTarget} onOpenChange={(v) => { if (!v) { setPinTarget(null); setPinDraft(''); } }}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-white">Set Kiosk PIN</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-zinc-400 text-sm font-sans">
              The 4-digit PIN <span className="text-white font-medium">{pinTarget?.name}</span> enters at the lounge kiosk to clock in / out. Share it with them privately.
            </p>
            <Input
              type="tel"
              maxLength={4}
              autoFocus
              value={pinDraft}
              onChange={(e) => setPinDraft(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="4-digit PIN"
              className="bg-transparent border border-white/15 text-white text-center text-2xl font-mono tracking-[0.5em] h-14"
              data-testid="kiosk-pin-input"
            />
            <Button onClick={savePin} disabled={pinSaving || pinDraft.length !== 4} className="w-full bg-gold text-black font-bold rounded-sm font-sans h-11" data-testid="save-kiosk-pin">
              {pinSaving ? 'Saving…' : 'Save PIN'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
